import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../lib/supabase';
import { createUser, deleteUser, updateUserPassword } from '../lib/adminUsers';
import { StaffDetailDrawer } from '../components/staff/StaffDetailDrawer';
import { StaffIndicatorCell, useStaffIndicators } from '../components/staff/StaffIndicators';
import StaffAvatar from '../components/shared/StaffAvatar';
import { AvatarBgSwatches } from '../components/shared/AvatarBgPicker';
import { ProfileAvatar } from '../components/shared/PersonAvatar';
import { COUNTRIES, parsePhone } from '../lib/phone';
import { publishAvatarBg, useAvatarBg, type AvatarBg } from '../lib/staffAvatar';
import { Button } from '../components/media/MediaUI';

// ─── Types ────────────────────────────────────────────────────────────────────

type UserRole   = 'customer' | 'staff' | 'admin' | 'investor';
type UserStatus = 'pending'  | 'active' | 'disabled' | 'bloked';

interface UserProfile {
  id: string;
  created_at: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  role: UserRole;
  status: UserStatus;
  nationality: string | null;
  birth_date: string | null;
  identity_number: string | null;
  address: string | null;
  avatar_url: string | null;
  avatar_bg?: string | null;
  gender: string | null;
}

interface EditForm {
  full_name: string;
  email: string;
  new_password: string;
  phone_dial: string;
  phone_number: string;
  role: UserRole;
  status: UserStatus;
  gender: string;
  nationality: string;
  birth_date: string;
  identity_number: string;
  address: string;
  avatar_file: File | null;
  avatar_preview: string | null;
  avatar_bg: AvatarBg;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(s: string): string {
  const d = new Date(s);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// ─── Badge configs ────────────────────────────────────────────────────────────

const ROLE_CFG: Record<UserRole, { label: string; color: string; bg: string }> = {
  admin:    { label: 'Admin',    color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
  staff:    { label: 'Staff',    color: '#3A74C2', bg: 'rgba(75,166,234,0.12)'  },
  investor: { label: 'Investor', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
  customer: { label: 'Customer', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

const STATUS_CFG: Record<UserStatus, { label: string; color: string; bg: string }> = {
  active:   { label: 'Active',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)'   },
  pending:  { label: 'Pending',  color: '#eab308', bg: 'rgba(234,179,8,0.12)'   },
  disabled: { label: 'Disabled', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  bloked:   { label: 'Blocked',  color: '#ef4444', bg: 'rgba(239,68,68,0.12)'   },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// Toast
interface ToastState { message: string; type: 'success' | 'error'; }
const Toast: React.FC<ToastState> = ({ message, type }) =>
  ReactDOM.createPortal(
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
      display: 'flex', alignItems: 'center', gap: 10,
      background: type === 'success' ? '#0f1117' : '#ef4444',
      color: '#fff', borderRadius: 12, padding: '12px 20px',
      fontSize: 14, fontWeight: 500,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'slideUpIn 200ms ease',
    }}>
      {type === 'success'
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="12" r="9" stroke="#4ade80" strokeWidth="1.8" /></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.8" /><path d="M12 8v4M12 16h.01" stroke="white" strokeWidth="1.8" strokeLinecap="round" /></svg>
      }
      {message}
    </div>,
    document.body,
  );

// Role badge
const RoleBadge: React.FC<{ role: UserRole }> = ({ role }) => {
  const cfg = ROLE_CFG[role];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg,
      borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// Status badge
const StatusBadge: React.FC<{ status: UserStatus }> = ({ status }) => {
  const cfg = STATUS_CFG[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontSize: 11, fontWeight: 600,
      color: cfg.color, background: cfg.bg,
      borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

// Table header cell
const Th: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ children, style, ...rest }) => (
  <th style={{
    padding: '9px 12px', fontSize: 11, fontWeight: 600,
    color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.7px',
    textAlign: 'left', background: '#fff',
    borderBottom: '1.5px solid #f0f0f0',
    position: 'sticky', top: 0, zIndex: 1,
    whiteSpace: 'nowrap', userSelect: 'none',
    ...style,
  }} {...rest}>
    {children}
  </th>
);

// Action icon button
const ActionBtn: React.FC<{
  onClick: () => void;
  title: string;
  hoverColor: string;
  children: React.ReactNode;
}> = ({ onClick, title, hoverColor, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      title={title}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 30, height: 30, borderRadius: 7, border: 'none',
        background: hovered ? `${hoverColor}18` : 'transparent',
        color: hovered ? hoverColor : '#9ca3af',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', transition: 'all 140ms ease', flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
};

// Skeleton row
const SkeletonRow: React.FC = () => (
  <tr>
    {[180, 160, 90, 80, 80, 90, 60].map((w, i) => (
      <td key={i} style={{ padding: '12px 12px' }}>
        <div style={{ height: 13, width: w, borderRadius: 6, background: '#f3f4f6', animation: 'pulse 1.5s ease-in-out infinite' }} />
      </td>
    ))}
  </tr>
);

// Field style
const FIELD_STYLE: React.CSSProperties = {
  width: '100%', height: 40, padding: '0 12px',
  fontSize: 14, color: '#0f1117',
  background: '#fff', border: '1.5px solid #e5e7eb',
  borderRadius: 8, outline: 'none', fontFamily: 'inherit',
  boxSizing: 'border-box', transition: 'border-color 150ms ease',
};

// ─── Edit User Modal ──────────────────────────────────────────────────────────

interface EditModalProps {
  user: UserProfile;
  onClose: () => void;
  onSaved: () => void;
}

const EditUserModal: React.FC<EditModalProps> = ({ user, onClose, onSaved }) => {
  const parsed = parsePhone(user.phone);
  const savedBg = useAvatarBg(user);

  const [form, setForm] = useState<EditForm>({
    full_name:       user.full_name       ?? '',
    email:           user.email           ?? '',
    new_password:    '',
    phone_dial:      parsed.dial,
    phone_number:    parsed.number,
    role:            user.role,
    status:          user.status,
    gender:          user.gender          ?? '',
    nationality:     user.nationality     ?? '',
    birth_date:      user.birth_date      ?? '',
    identity_number: user.identity_number ?? '',
    address:         user.address         ?? '',
    avatar_file:     null,
    avatar_preview:  user.avatar_url,
    avatar_bg:       savedBg,
  });

  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pwVisible, setPwVisible] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be under 5 MB.');
      return;
    }
    const preview = URL.createObjectURL(file);
    setForm(f => ({ ...f, avatar_file: file, avatar_preview: preview }));
  };

  const handleGeneratePassword = () => {
    const pw = generatePassword();
    set('new_password', pw);
    setPwVisible(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      setFormError('Full name and email are required.');
      return;
    }
    setFormError(null);
    setSaving(true);

    try {
      // 1. Upload avatar if changed
      let avatar_url = user.avatar_url;
      if (form.avatar_file) {
        const ext      = form.avatar_file.name.split('.').pop() ?? 'jpg';
        const fileName = `${form.full_name.trim().replace(/\s+/g, '-').toLowerCase()}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('avatars')
          .upload(fileName, form.avatar_file, { upsert: true });
        if (upErr) throw upErr;
        const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(fileName);
        avatar_url = publicUrl;
      }

      // 2. Update profile row
      const phone = form.phone_number.trim()
        ? `${form.phone_dial}${form.phone_number.trim()}`
        : null;

      const { error: profileErr } = await supabase
        .from('profiles')
        .update({
          full_name:       form.full_name.trim()       || null,
          email:           form.email.trim()           || null,
          phone,
          role:            form.role,
          status:          form.status,
          gender:          form.gender                 || null,
          nationality:     form.nationality.trim()     || null,
          birth_date:      form.birth_date             || null,
          identity_number: form.identity_number.trim() || null,
          address:         form.address.trim()         || null,
          avatar_url,
          avatar_bg:       form.avatar_bg,
        })
        .eq('id', user.id);

      if (profileErr) throw profileErr;
      publishAvatarBg(user.id, form.avatar_bg);

      // 3. Update password through the admin-users Edge Function — this needs
      //    the service_role key, which stays on the server.
      if (form.new_password.trim()) {
        await updateUserPassword(user.id, form.new_password.trim());
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : 'Failed to save changes.');
      setSaving(false);
    }
  };

  const dialCountry = COUNTRIES.find(c => c.dial === form.phone_dial) ?? COUNTRIES[0];
  const avatarProfile = { avatar_url: form.avatar_preview, gender: form.gender, avatar_bg: form.avatar_bg };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,17,23,0.45)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, animation: 'fadeIn 150ms ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 18, width: '100%', maxWidth: 600,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
        animation: 'slideUp 180ms ease',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0f1117', letterSpacing: '-0.3px' }}>
                Edit User
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                Update profile details and access settings
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── Avatar section ── */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18,
            padding: '16px 18px', background: 'var(--surface)', borderRadius: 12,
            border: '1px solid var(--border-subtle)', marginBottom: 20,
          }}>
            {/* Circle preview */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <StaffAvatar
                profile={avatarProfile}
                alt={form.full_name || user.full_name || ''}
                size={64}
              />
            </div>

            {/* Upload info */}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f1117', marginBottom: 4 }}>
                Profile Photo
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
                JPG, PNG or WebP · Max 5 MB
              </div>
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                style={{
                  height: 32, padding: '0 14px', borderRadius: 8,
                  border: '1px solid #EBEBEB', background: '#fff',
                  fontSize: 12, fontWeight: 600, color: 'var(--foreground)',
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'border-color 140ms ease',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#4ba6ea'; (e.currentTarget as HTMLButtonElement).style.color = '#3A74C2'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)'; }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Upload Photo
              </button>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }}
                onChange={handleAvatarChange}
              />
            </div>

            {(form.role === 'staff' || form.role === 'admin') && (
              <AvatarBgSwatches
                profile={avatarProfile}
                value={form.avatar_bg}
                onChange={(bg) => set('avatar_bg', bg)}
                className="w-full"
              />
            )}
          </div>

          {/* ── Form error ── */}
          {formError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 16, padding: '10px 14px',
              background: '#fef2f2', borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8" /><path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" /></svg>
              <span style={{ fontSize: 13, color: '#ef4444' }}>{formError}</span>
            </div>
          )}

          {/* ── Fields grid ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>

            {/* Email — full width */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Email <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="email" required
                value={form.email}
                onChange={e => set('email', e.target.value)}
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>

            {/* New Password — full width */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                New Password
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type={pwVisible ? 'text' : 'password'}
                    value={form.new_password}
                    onChange={e => set('new_password', e.target.value)}
                    placeholder="Leave blank to keep current password"
                    style={{ ...FIELD_STYLE, paddingRight: 40 }}
                    onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                    onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setPwVisible(v => !v)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0,
                      display: 'flex', alignItems: 'center',
                    }}
                  >
                    {pwVisible
                      ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><path d="M1 1l22 22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      : <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="1.8" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" /></svg>
                    }
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  style={{
                    height: 40, padding: '0 14px', borderRadius: 8, flexShrink: 0,
                    border: '1px solid #EBEBEB', background: '#fff',
                    fontSize: 13, fontWeight: 600, color: 'var(--foreground)',
                    cursor: 'pointer', fontFamily: 'inherit',
                    transition: 'all 140ms ease', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#4ba6ea'; (e.currentTarget as HTMLButtonElement).style.color = '#3A74C2'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)'; }}
                >
                  Generate
                </button>
              </div>
              <p style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 5, lineHeight: 1.4 }}>
                Only fill this if you want to change the password
              </p>
            </div>

            {/* Full Name */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Full Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text" required
                value={form.full_name}
                onChange={e => set('full_name', e.target.value)}
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>

            {/* Phone with country code */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Phone
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                {/* Dial code selector */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <select
                    value={form.phone_dial}
                    onChange={e => set('phone_dial', e.target.value)}
                    style={{
                      height: 40, padding: '0 28px 0 10px',
                      fontSize: 13, color: '#0f1117',
                      background: '#fff', border: '1.5px solid #e5e7eb',
                      borderRadius: 8, outline: 'none', fontFamily: 'inherit',
                      cursor: 'pointer', appearance: 'none', minWidth: 90,
                      transition: 'border-color 150ms ease',
                    }}
                    onFocus={e => { (e.target as HTMLSelectElement).style.borderColor = '#4ba6ea'; }}
                    onBlur={e => { (e.target as HTMLSelectElement).style.borderColor = '#e5e7eb'; }}
                  >
                    {COUNTRIES.map(c => (
                      <option key={c.code} value={c.dial}>
                        {c.flag} {c.dial}
                      </option>
                    ))}
                  </select>
                  {/* Chevron */}
                  <svg
                    width="10" height="10" viewBox="0 0 24 24" fill="none"
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}
                  >
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>

                {/* Number input */}
                <input
                  type="tel"
                  value={form.phone_number}
                  onChange={e => set('phone_number', e.target.value)}
                  placeholder={`${dialCountry.flag} ${form.phone_dial} …`}
                  style={{ ...FIELD_STYLE, flex: 1 }}
                  onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                  onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Role
              </label>
              <select
                value={form.role}
                onChange={e => set('role', e.target.value as UserRole)}
                style={{ ...FIELD_STYLE, cursor: 'pointer' }}
                onFocus={e => { (e.target as HTMLSelectElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLSelectElement).style.borderColor = '#e5e7eb'; }}
              >
                <option value="customer">Customer</option>
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="investor">Investor</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Status
              </label>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value as UserStatus)}
                style={{ ...FIELD_STYLE, cursor: 'pointer' }}
                onFocus={e => { (e.target as HTMLSelectElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLSelectElement).style.borderColor = '#e5e7eb'; }}
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="bloked">Blocked</option>
              </select>
            </div>

            {/* Gender */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Gender
              </label>
              <select
                value={form.gender}
                onChange={e => set('gender', e.target.value)}
                style={{ ...FIELD_STYLE, cursor: 'pointer' }}
                onFocus={e => { (e.target as HTMLSelectElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLSelectElement).style.borderColor = '#e5e7eb'; }}
              >
                <option value="">Not set</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Nationality */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Nationality
              </label>
              <input
                type="text"
                value={form.nationality}
                onChange={e => set('nationality', e.target.value)}
                placeholder="e.g. Turkish"
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>

            {/* Birth Date */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Birth Date
              </label>
              <input
                type="date"
                value={form.birth_date}
                onChange={e => set('birth_date', e.target.value)}
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>

            {/* Identity Number */}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Identity Number
              </label>
              <input
                type="text"
                value={form.identity_number}
                onChange={e => set('identity_number', e.target.value)}
                placeholder="TC / Passport number"
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>

            {/* Address — full width */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                Address
              </label>
              <input
                type="text"
                value={form.address}
                onChange={e => set('address', e.target.value)}
                placeholder="Street, City, Country"
                style={FIELD_STYLE}
                onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
              />
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 10,
            marginTop: 24, paddingTop: 18, borderTop: '1px solid #f3f4f6',
          }}>
            <button
              type="button" onClick={onClose}
              style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid #EBEBEB', background: '#fff', fontSize: 14, fontWeight: 500, color: 'var(--foreground)', cursor: 'pointer', fontFamily: 'inherit' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#9ca3af'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; }}
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={saving}
              style={{ padding: '9px 22px', fontSize: 14, fontFamily: 'inherit' }}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

// ─── Create User Modal ───────────────────────────────────────────────────────

interface CreateForm {
  email:     string;
  password:  string;
  full_name: string;
  role:      'admin' | 'staff';
}

const EMPTY_CREATE: CreateForm = { email: '', password: '', full_name: '', role: 'staff' };

interface CreateModalProps {
  onClose:  () => void;
  onSaved:  () => void;
  onToast:  (msg: string, type: 'success' | 'error') => void;
}

const CreateUserModal: React.FC<CreateModalProps> = ({ onClose, onSaved, onToast }) => {
  const [form,      setForm]      = useState<CreateForm>(EMPTY_CREATE);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pwVisible, setPwVisible] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const set = <K extends keyof CreateForm>(key: K, value: CreateForm[K]) =>
    setForm(f => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim() || !form.full_name.trim()) {
      setFormError('All fields are required.');
      return;
    }
    setFormError(null);
    setSaving(true);

    try {
      // Creating the auth user and stamping its profile row both happen inside
      // the admin-users Edge Function, so a failed profile write rolls the auth
      // user back instead of leaving a half-provisioned account behind.
      await createUser({
        email:    form.email.trim(),
        password: form.password.trim(),
        fullName: form.full_name.trim(),
        role:     form.role,
      });

      onSaved();
      onToast(`User ${form.email.trim()} created successfully.`, 'success');
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create user.';
      setFormError(msg);
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,17,23,0.45)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, animation: 'fadeIn 150ms ease',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 18, width: '100%', maxWidth: 460,
        boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.06)',
        animation: 'slideUp 180ms ease', display: 'flex', flexDirection: 'column',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#0f1117', letterSpacing: '-0.3px' }}>
                Add User
              </div>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                Create a new staff or admin account
              </div>
            </div>
            <button
              type="button" onClick={onClose}
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#e5e7eb'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Error banner */}
          {formError && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '10px 14px', background: '#fef2f2',
              borderRadius: 8, border: '1px solid rgba(239,68,68,0.2)',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                <circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8" />
                <path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <span style={{ fontSize: 13, color: '#ef4444', lineHeight: 1.45 }}>{formError}</span>
            </div>
          )}

          {/* Email */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Email <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="email" required autoFocus
              value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder="name@company.com"
              style={FIELD_STYLE}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
            />
          </div>

          {/* Password */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Password <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <input
                  type={pwVisible ? 'text' : 'password'} required
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  placeholder="Min. 8 characters"
                  style={{ ...FIELD_STYLE, paddingRight: 40 }}
                  onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
                  onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
                />
                <button
                  type="button" onClick={() => setPwVisible(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}
                >
                  {pwVisible
                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><path d="M1 1l22 22" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="1.8"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/></svg>
                  }
                </button>
              </div>
              <button
                type="button"
                onClick={() => { const pw = generatePassword(); set('password', pw); setPwVisible(true); }}
                style={{
                  height: 40, padding: '0 14px', borderRadius: 8, flexShrink: 0,
                  border: '1px solid #EBEBEB', background: '#fff',
                  fontSize: 13, fontWeight: 600, color: 'var(--foreground)',
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 140ms ease', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#4ba6ea'; (e.currentTarget as HTMLButtonElement).style.color = '#3A74C2'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; (e.currentTarget as HTMLButtonElement).style.color = 'var(--foreground)'; }}
              >
                Generate
              </button>
            </div>
          </div>

          {/* Full Name */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Full Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text" required
              value={form.full_name}
              onChange={e => set('full_name', e.target.value)}
              placeholder="First and last name"
              style={FIELD_STYLE}
              onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
              onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
            />
          </div>

          {/* Role */}
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
              Role
            </label>
            <select
              value={form.role}
              onChange={e => set('role', e.target.value as 'admin' | 'staff')}
              style={{ ...FIELD_STYLE, cursor: 'pointer' }}
              onFocus={e => { (e.target as HTMLSelectElement).style.borderColor = '#4ba6ea'; }}
              onBlur={e => { (e.target as HTMLSelectElement).style.borderColor = '#e5e7eb'; }}
            >
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 6, paddingTop: 16, borderTop: '1px solid #f3f4f6' }}>
            <button
              type="button" onClick={onClose}
              style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid #EBEBEB', background: '#fff', fontSize: 14, fontWeight: 500, color: 'var(--foreground)', cursor: 'pointer', fontFamily: 'inherit' }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#9ca3af'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; }}
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={saving}
              style={{ padding: '9px 22px', fontSize: 14, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}
            >
              {saving ? (
                <>
                  <svg style={{ animation: 'spin 0.7s linear infinite' }} width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Creating…
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                  </svg>
                  Create User
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const UsersPage: React.FC = () => {
  const indicators = useStaffIndicators();
  const [detailUser, setDetailUser] = useState<UserProfile | null>(null);
  const [users,    setUsers]    = useState<UserProfile[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [search,   setSearch]   = useState('');
  const [editing,      setEditing]      = useState<UserProfile | null>(null);
  const [creating,     setCreating]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserProfile | null>(null);
  const [deleting,     setDeleting]     = useState(false);
  const [toast,        setToast]        = useState<ToastState | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3200);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('profiles')
      .select('id, created_at, full_name, email, phone, role, status, nationality, birth_date, identity_number, address, avatar_url, avatar_bg, gender')
      .order('created_at', { ascending: false });

    if (err) { setError(err.message); setLoading(false); return; }
    setUsers((data as UserProfile[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      showToast('User deleted successfully.', 'success');
      fetchUsers();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to delete user.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, showToast, fetchUsers]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q
      || (u.full_name ?? '').toLowerCase().includes(q)
      || (u.email ?? '').toLowerCase().includes(q);
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', padding: '44px 40px' }}>
      <style>{`
        @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes slideUpIn{ from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
        @keyframes spin     { to{transform:rotate(360deg)} }
        .user-row:hover { background: rgba(75,166,234,0.03) !important; }
      `}</style>

      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ba6ea' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', letterSpacing: '1px', textTransform: 'uppercase' }}>
            Management
          </span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 500, color: '#0f1117', letterSpacing: '-0.8px', margin: 0 }}>
          Users
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', marginTop: 4 }}>
          Manage user profiles and access
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 12, padding: '12px 16px', marginBottom: 24,
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#ef4444',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#ef4444" strokeWidth="1.8" /><path d="M12 8v4M12 16h.01" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" /></svg>
          {error}
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        background: 'var(--surface)', borderRadius: 14, padding: '14px 18px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid var(--border-subtle)',
        marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 360 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', pointerEvents: 'none',
          }}>
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
            <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', height: 38, paddingLeft: 34, paddingRight: 12,
              fontSize: 13, color: '#0f1117', background: '#f9fafb',
              border: '1.5px solid #e5e7eb', borderRadius: 9, outline: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 150ms ease',
            }}
            onFocus={e => { (e.target as HTMLInputElement).style.borderColor = '#4ba6ea'; }}
            onBlur={e => { (e.target as HTMLInputElement).style.borderColor = '#e5e7eb'; }}
          />
        </div>

        <div style={{ flex: 1 }} />

        <div style={{ fontSize: 13, color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {loading ? '…' : `${filtered.length} user${filtered.length !== 1 ? 's' : ''}`}
        </div>

        <Button
          onClick={() => setCreating(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '0 16px', fontSize: 13, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
          </svg>
          Add User
        </Button>
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--surface)', borderRadius: 14, overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid var(--border-subtle)',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Indicators</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th>Created At</Th>
                <Th style={{ textAlign: 'right' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                : filtered.length === 0
                  ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '52px 24px', textAlign: 'center' }}>
                        <div style={{ fontSize: 14, color: '#9ca3af' }}>
                          {search ? 'No users match your search.' : 'No users found.'}
                        </div>
                      </td>
                    </tr>
                  )
                  : filtered.map((user) => (
                    <tr
                      key={user.id}
                      className="user-row"
                      style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)', transition: 'background 100ms ease' }}
                    >
                      {/* Name + Avatar */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <ProfileAvatar profile={user} alt={user.full_name ?? ''} size={34} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f1117', lineHeight: 1.3 }}>
                              {user.full_name || <span style={{ color: '#9ca3af', fontWeight: 400 }}>—</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{user.email || '—'}</span>
                      </td>

                      {/* Indicators — staff only */}
                      <td style={{ padding: '10px 12px' }}>
                        {user.role === 'staff' ? (
                          <button
                            type="button"
                            onClick={() => setDetailUser(user)}
                            aria-label={`Indicators for ${user.full_name ?? user.email ?? 'staff member'}`}
                            style={{ minHeight: 44, border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                          >
                            <StaffIndicatorCell goals={indicators.byProfile[user.id]} indicators={indicators} />
                          </button>
                        ) : <span style={{ fontSize: 13, color: '#d1d5db' }}>—</span>}
                      </td>

                      {/* Role */}
                      <td style={{ padding: '10px 12px' }}>
                        <RoleBadge role={user.role} />
                      </td>

                      {/* Status */}
                      <td style={{ padding: '10px 12px' }}>
                        <StatusBadge status={user.status} />
                      </td>

                      {/* Created At */}
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          {formatDateTime(user.created_at)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                          <ActionBtn onClick={() => setEditing(user)} title="Edit user" hoverColor="#4ba6ea">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </ActionBtn>
                          <ActionBtn onClick={() => setDeleteTarget(user)} title="Delete user" hoverColor="#ef4444">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                          </ActionBtn>
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* Table footer */}
        {!loading && filtered.length > 0 && (
          <div style={{
            padding: '12px 18px', borderTop: '1px solid #f5f5f5',
            fontSize: 12, color: '#9ca3af',
          }}>
            {filtered.length} of {users.length} users
          </div>
        )}
      </div>

      {/* Create modal */}
      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={fetchUsers}
          onToast={showToast}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            fetchUsers();
            showToast('User updated successfully.', 'success');
          }}
        />
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && ReactDOM.createPortal(
        <div
          onClick={() => { if (!deleting) setDeleteTarget(null); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(15,17,23,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, animation: 'fadeIn 150ms ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 18, width: '100%', maxWidth: 400,
              boxShadow: '0 24px 80px rgba(0,0,0,0.20), 0 0 0 1px rgba(0,0,0,0.06)',
              animation: 'slideUp 180ms ease', overflow: 'hidden',
            }}
          >
            {/* Red top bar */}
            <div style={{ height: 4, background: '#ef4444' }} />

            <div style={{ padding: '24px 24px 20px' }}>
              {/* Icon + title */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(239,68,68,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 11v6M14 11v6" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0f1117', marginBottom: 5 }}>
                    Delete User
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.55 }}>
                    Are you sure you want to delete{' '}
                    <span style={{ fontWeight: 600, color: '#0f1117' }}>
                      {deleteTarget.full_name || deleteTarget.email || 'this user'}
                    </span>
                    ? This cannot be undone.
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setDeleteTarget(null)}
                  style={{
                    padding: '9px 18px', borderRadius: 9,
                    border: '1px solid #EBEBEB', background: '#fff',
                    fontSize: 14, fontWeight: 500, color: 'var(--foreground)',
                    cursor: deleting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => { if (!deleting) (e.currentTarget as HTMLButtonElement).style.borderColor = '#9ca3af'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = '#EBEBEB'; }}
                >
                  Cancel
                </button>
                <Button
                  variant="danger"
                  type="button"
                  disabled={deleting}
                  onClick={handleDelete}
                  style={{ padding: '9px 22px', fontSize: 14, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}
                >
                  {deleting ? (
                    <>
                      <svg style={{ animation: 'spin 0.7s linear infinite' }} width="13" height="13" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      Deleting…
                    </>
                  ) : 'Delete User'}
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}

      {detailUser && (
        <StaffDetailDrawer
          profileId={detailUser.id}
          name={detailUser.full_name || detailUser.email || 'Staff member'}
          avatar={detailUser}
          onClose={() => setDetailUser(null)}
        />
      )}
    </div>
  );
};

export default UsersPage;
